(() => {
  'use strict';

  function init() {
    const id = 'WVGuaranteeHover';
    if (document.getElementById(id)) return;
    const selector = 'a.wv-guarantee__label[data-wv-guarantee-open]';
    const hoverMedia = window.matchMedia('(any-hover: hover) and (any-pointer: fine)');
    const dialog = document.getElementById('WVGuaranteeDialog');
    const panel = document.createElement('div');
    panel.id = id;
    panel.className = 'wv-guarantee-hover';
    panel.hidden = true;
    panel.tabIndex = 0;
    panel.setAttribute('role', 'region');
    const bar = document.createElement('div');
    bar.className = 'wv-guarantee-hover__bar';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'wv-guarantee-hover__close';
    // Reuse the theme's translated accessible name, without changing its copy.
    close.setAttribute('aria-label', dialog?.querySelector('[data-wv-guarantee-close]')?.getAttribute('aria-label') || 'GARAN');
    close.textContent = '\u00d7';
    const image = document.createElement('img');
    image.className = 'wv-guarantee-hover__image';
    image.decoding = 'async';
    bar.append(close);
    panel.append(bar, image);
    const bridge = document.createElement('div');
    bridge.className = 'wv-guarantee-hover__bridge';
    bridge.hidden = true;
    bridge.setAttribute('aria-hidden', 'true');
    document.body.append(bridge, panel);

    let active = null;
    let dismissed = null;
    let modalOpener = null;
    let oldControls = null;
    let timer;
    let frame;
    let pointerType = '';
    let lastTouch = -Infinity;
    const contains = (node, target) => target instanceof Node && Boolean(node?.contains(target));
    const focused = node => contains(node, document.activeElement);
    const hovered = node => Boolean(node?.isConnected && node.matches(':hover'));
    const title = link => link.dataset.guaranteeTitle || link.querySelector('img')?.alt || link.getAttribute('aria-label') || 'GARAN';

    function hide(suppress = false) {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      observer.disconnect();
      if (!active) return;
      const previous = active;
      const returnFocus = focused(panel);
      if (suppress || returnFocus) dismissed = previous;
      if (oldControls === null) previous.removeAttribute('aria-controls');
      else previous.setAttribute('aria-controls', oldControls);
      active = null;
      panel.hidden = true;
      bridge.hidden = true;
      if (returnFocus && previous.isConnected) previous.focus({preventScroll: true});
    }

    function position() {
      if (!active?.isConnected || !active.getClientRects().length || active.closest('[hidden]') || dialog?.open) return hide(true);
      const box = active.getBoundingClientRect();
      const viewport = window.visualViewport;
      const originX = viewport?.offsetLeft || 0;
      const originY = viewport?.offsetTop || 0;
      const width = viewport?.width || document.documentElement.clientWidth;
      const height = viewport?.height || window.innerHeight;
      const minX = originX + 12;
      const minY = originY + 12;
      const maxX = originX + width - 12;
      const maxY = originY + height - 12;
      if (box.bottom < minY || box.top > maxY || box.right < minX || box.left > maxX) {
        bridge.hidden = true;
        if (!focused(active) && !focused(panel)) hide();
        return;
      }
      const gap = 10;
      let panelWidth = Math.min(540, Math.max(1, maxX - minX));
      let panelHeight = Math.max(1, maxY - minY);
      const rightSpace = maxX - box.right - gap;
      const leftSpace = box.left - minX - gap;
      let left;
      let top;
      if (Math.max(rightSpace, leftSpace) >= Math.min(320, panelWidth)) {
        const right = rightSpace >= panelWidth || rightSpace >= leftSpace;
        panelWidth = Math.min(panelWidth, right ? rightSpace : leftSpace);
        left = right ? box.right + gap : box.left - gap - panelWidth;
        top = box.top;
      } else {
        left = Math.max(minX, Math.min(box.left, maxX - panelWidth));
        const below = maxY - box.bottom - gap;
        const above = box.top - gap - minY;
        const placeBelow = below >= above;
        panelHeight = Math.max(1, placeBelow ? below : above);
        top = placeBelow ? box.bottom + gap : null;
      }
      panel.style.width = panelWidth + 'px';
      panel.style.maxHeight = panelHeight + 'px';
      const actualHeight = panel.getBoundingClientRect().height;
      if (top === null) top = box.top - gap - actualHeight;
      panel.style.left = Math.max(minX, Math.min(left, maxX - panelWidth)) + 'px';
      panel.style.top = Math.max(minY, Math.min(top, maxY - actualHeight)) + 'px';

      // A narrow hit area spans only the gap, so slow crossings stay open.
      const p = panel.getBoundingClientRect();
      let b;
      if (p.right <= box.left || p.left >= box.right) {
        b = {left: Math.min(p.right, box.right) - 1, top: Math.max(p.top, box.top),
          width: Math.max(p.left - box.right, box.left - p.right) + 2,
          height: Math.max(0, Math.min(p.bottom, box.bottom) - Math.max(p.top, box.top))};
      } else {
        b = {left: Math.max(p.left, box.left), top: Math.min(p.bottom, box.bottom) - 1,
          width: Math.max(0, Math.min(p.right, box.right) - Math.max(p.left, box.left)),
          height: Math.max(p.top - box.bottom, box.top - p.bottom, 0) + 2};
      }
      Object.assign(bridge.style, {left: b.left + 'px', top: b.top + 'px', width: b.width + 'px', height: b.height + 'px'});
      bridge.hidden = b.width <= 0 || b.height <= 0;
    }

    function refresh() {
      if (!active) return;
      if (image.src !== active.href) {
        image.src = active.href;
        panel.scrollTop = 0;
      }
      image.alt = title(active);
      panel.setAttribute('aria-label', image.alt);
      position();
    }

    const reposition = () => {
      if (!active) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(refresh);
    };
    // Observe only while open: variant re-renders must not leave stale artwork.
    const observer = new MutationObserver(reposition);

    function show(link) {
      clearTimeout(timer);
      if (link === dismissed || link === modalOpener || dialog?.open || !link.getClientRects().length || link.closest('[hidden]')) return;
      if (active === link) return;
      hide();
      active = link;
      oldControls = link.getAttribute('aria-controls');
      link.setAttribute('aria-controls', [oldControls, id].filter(Boolean).join(' '));
      panel.hidden = false;
      panel.scrollTop = 0;
      refresh();
      if (active) observer.observe(document.body, {childList: true, subtree: true, attributes: true,
        attributeFilter: ['hidden', 'href', 'data-guarantee-title', 'open']});
    }

    function scheduleHide() {
      if (!active) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!hovered(active) && !hovered(panel) && !hovered(bridge) && !focused(active) && !focused(panel)) hide();
      }, 240);
    }

    document.addEventListener('pointerover', event => {
      if (event.pointerType !== 'mouse' || !hoverMedia.matches || performance.now() - lastTouch < 800) return;
      const link = event.target.closest?.(selector);
      if (link && !contains(link, event.relatedTarget)) show(link);
    });
    document.addEventListener('pointerout', event => {
      const link = event.target.closest?.(selector);
      if (link && !contains(link, event.relatedTarget)) {
        if (dismissed === link && !dialog?.open) dismissed = null;
        scheduleHide();
      }
    });
    document.addEventListener('pointermove', event => {
      if (modalOpener && !dialog?.open && !contains(modalOpener, event.target)) {
        if (dismissed === modalOpener) dismissed = null;
        modalOpener = null;
      }
    }, {passive: true});
    document.addEventListener('pointerdown', event => {
      pointerType = event.pointerType;
      if (pointerType === 'touch' || pointerType === 'pen') {
        lastTouch = performance.now();
        hide(true);
      }
    }, true);
    document.addEventListener('focusin', event => {
      const link = event.target.closest?.(selector);
      if (link && pointerType !== 'touch' && pointerType !== 'pen' && performance.now() - lastTouch >= 800) show(link);
      if (focused(panel)) clearTimeout(timer);
    });
    document.addEventListener('focusout', event => {
      if (dismissed && contains(dismissed, event.target) && !contains(dismissed, event.relatedTarget) && !dialog?.open) dismissed = null;
      if (active && (contains(active, event.target) || contains(panel, event.target))) scheduleHide();
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Tab') {
        pointerType = '';
        lastTouch = -Infinity;
        dismissed = null;
        if (!dialog?.open) modalOpener = null;
        // Make the scroll region reachable directly from its label.
        if (active && document.activeElement === active && !event.shiftKey) {
          event.preventDefault();
          panel.focus({preventScroll: true});
        } else if (active && ((document.activeElement === panel && event.shiftKey) || (document.activeElement === close && !event.shiftKey))) {
          const link = active;
          hide(true);
          link.focus({preventScroll: true});
          if (event.shiftKey) event.preventDefault();
        }
      }
      if (event.key === 'Escape' && active && !dialog?.open) {
        if (focused(active) || focused(panel)) event.preventDefault();
        hide(true);
      }
    }, true);
    document.addEventListener('click', event => {
      const link = event.target.closest?.(selector);
      if (link) {
        hide(true);
        dismissed = link;
        modalOpener = link;
      } else if (active && !contains(panel, event.target)) hide();
      // Do not preventDefault or stop propagation: the existing dialog owns clicks.
    }, true);
    for (const surface of [panel, bridge]) {
      surface.addEventListener('pointerenter', () => clearTimeout(timer));
      surface.addEventListener('pointerleave', scheduleHide);
    }
    close.addEventListener('click', () => hide(true));
    // Clamp the new intrinsic height during load, before the next paint.
    image.addEventListener('load', refresh);
    image.addEventListener('error', () => hide(true));
    window.addEventListener('resize', reposition, {passive: true});
    document.addEventListener('scroll', event => {
      if (!contains(panel, event.target)) reposition();
    }, {passive: true, capture: true});
    window.visualViewport?.addEventListener('resize', reposition, {passive: true});
    window.visualViewport?.addEventListener('scroll', reposition, {passive: true});
    window.addEventListener('blur', () => hide(true));
    hoverMedia.addEventListener('change', () => { if (!hoverMedia.matches) hide(true); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
